import { useState } from 'react';
import { useAppStore } from '@/store/appStore';
import { Sidebar } from './Sidebar';
import { CommandPalette } from '@/components/shared/CommandPalette';
import { SettingsModal } from '@/components/modals/SettingsModal';
import { SpaceSettingsModal } from '@/components/modals/SpaceSettingsModal';
import { ListSettingsModal } from '@/components/modals/ListSettingsModal';
import { CreateTaskModal } from '@/components/modals/CreateTaskModal';
import { CreateSpaceModal } from '@/components/modals/CreateSpaceModal';
import { CreateOrgModal } from '@/components/modals/CreateOrgModal';
import { OrgSettingsModal } from '@/components/modals/OrgSettingsModal';
import { CacheVerification } from '@/components/shared/CacheVerification';
import { PendingInvitesBanner } from '@/components/PendingInvitesBanner';
import { Keyboard } from 'lucide-react';
import { isTauriMacOS } from '@/utils/platform';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  // Mobile state
  const isMobile = useAppStore((state) => state.isMobile);
  const isMobileSidebarOpen = useAppStore((state) => state.isMobileSidebarOpen);
  const setMobileSidebarOpen = useAppStore((state) => state.setMobileSidebarOpen);

  // User settings modal
  const isUserSettingsOpen = useAppStore((state) => state.isUserSettingsOpen);
  const closeUserSettings = useAppStore((state) => state.closeUserSettings);
  const theme = useAppStore((state) => state.theme);
  const palette = useAppStore((state) => state.palette);
  const setTheme = useAppStore((state) => state.setTheme);
  const setPalette = useAppStore((state) => state.setPalette);
  const userSettingsTab = useAppStore((state) => state.userSettingsTab);

  // Space settings modal
  const isSpaceSettingsOpen = useAppStore((state) => state.isSpaceSettingsOpen);
  const selectedSpaceName = useAppStore((state) => state.selectedProjectName);
  const selectedSpaceId = useAppStore((state) => state.selectedProjectId);

  // List settings modal
  const isListSettingsOpen = useAppStore((state) => state.isListSettingsOpen);
  const selectedListName = useAppStore((state) => state.selectedListName);
  const selectedFolderName = useAppStore((state) => state.selectedFolderName);
  const selectedListId = useAppStore((state) => state.selectedListId);
  const selectedFolderId = useAppStore((state) => state.selectedFolderId);

  // Coordinated close (modal + nav reset)
  const closeSettingsAndResetNavigation = useAppStore((state) => state.closeSettingsAndResetNavigation);

  // Create modals
  const isCreateTaskOpen = useAppStore((state) => state.isCreateTaskOpen);
  const closeCreateTask = useAppStore((state) => state.closeCreateTask);
  const isCreateSpaceOpen = useAppStore((state) => state.isCreateSpaceOpen);
  const closeCreateSpace = useAppStore((state) => state.closeCreateSpace);

  // Cache verification
  const showCacheVerification = useAppStore((state) => state.showCacheVerification);
  const closeCacheVerification = useAppStore((state) => state.closeCacheVerification);

  // Organization modals
  const [isCreateOrgOpen, setCreateOrgOpen] = useState(false);
  const [isOrgSettingsOpen, setOrgSettingsOpen] = useState(false);

  return (
    <div className="h-screen flex overflow-hidden bg-surface-primary relative">
      {/* Mobile sidebar as overlay */}
      {isMobile && isMobileSidebarOpen && (
        <div className="fixed inset-0 z-40 flex">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setMobileSidebarOpen(false)}
            aria-label="Chiudi navigazione"
          />
          <div className="relative w-[82vw] max-w-[320px] h-full shadow-2xl">
            <Sidebar isMobile onClose={() => setMobileSidebarOpen(false)} onCreateOrg={() => setCreateOrgOpen(true)} onOrgSettings={() => setOrgSettingsOpen(true)} />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden md:block h-full">
        <Sidebar onCreateOrg={() => setCreateOrgOpen(true)} onOrgSettings={() => setOrgSettingsOpen(true)} />
      </div>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden min-w-0">
        {isTauriMacOS() && (
          <div className="h-[38px] flex-shrink-0" data-tauri-drag-region="" />
        )}
        <PendingInvitesBanner />
        {children}

        {/* Keyboard shortcuts hint */}
        <div className="absolute bottom-4 right-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-secondary/80 border border-border-primary backdrop-blur-sm opacity-50 hover:opacity-100 transition-opacity">
          <Keyboard className="w-3.5 h-3.5 text-text-quaternary" />
          <span className="text-[10px] text-text-quaternary font-medium">
            <kbd className="kbd mr-1">⌘N</kbd>Nuovo
            <kbd className="kbd mx-1">⌘K</kbd>Cerca
            <kbd className="kbd mx-1">⌘1-7</kbd>Viste
          </span>
        </div>
      </main>

      {/* Command Palette */}
      <CommandPalette />

      {/* User Settings Modal */}
      <SettingsModal
        isOpen={isUserSettingsOpen}
        onClose={closeUserSettings}
        theme={theme}
        palette={palette}
        onThemeChange={setTheme}
        onPaletteChange={setPalette}
        initialTab={userSettingsTab}
      />

      {/* Space Settings Modal */}
      <SpaceSettingsModal
        isOpen={isSpaceSettingsOpen}
        onClose={() => closeSettingsAndResetNavigation('space')}
        spaceName={selectedSpaceName}
        spaceId={selectedSpaceId}
      />

      {/* List/Folder Settings Modal */}
      <ListSettingsModal
        isOpen={isListSettingsOpen}
        onClose={() => closeSettingsAndResetNavigation('list')}
        listName={selectedListName}
        folderName={selectedFolderName}
        spaceName={selectedSpaceName}
        spaceId={selectedSpaceId}
        listId={selectedListId}
        folderId={selectedFolderId}
      />

      {/* Create Task Modal */}
      <CreateTaskModal isOpen={isCreateTaskOpen} onClose={closeCreateTask} />

      <CreateSpaceModal isOpen={isCreateSpaceOpen} onClose={closeCreateSpace} />

      {/* Organization Modals */}
      <CreateOrgModal isOpen={isCreateOrgOpen} onClose={() => setCreateOrgOpen(false)} />
      <OrgSettingsModal isOpen={isOrgSettingsOpen} onClose={() => setOrgSettingsOpen(false)} />

      {/* Cache Verification Modal */}
      {showCacheVerification && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-surface-primary rounded-lg shadow-2xl border border-border-primary w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border-primary">
              <h2 className="text-lg font-semibold text-text-primary">
                Verifica Sistema di Caching
              </h2>
              <button
                onClick={closeCacheVerification}
                className="text-text-tertiary hover:text-text-primary transition-colors"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <CacheVerification />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
